import React, { useRef } from "react";
import { useContactForm } from "../hooks/useContactForm";
import { typography, spacing } from "../styles/designSystem";
import { getScrollVisibility, useScrollProgress } from "../styles/animations";
import ContactForm from "../components/ContactForm";

// ─── Component ───────────────────────────────────────────────────────────────

const Contact = () => {
  const { formData, status, handleChange, handleSubmit } = useContactForm({
    name: "",
    email: "",
    phone: "",
    service: "",
    budget: "",
    referralSource: "",
    message: "",
  });

  const sectionRef = useRef(null);
  const progress = useScrollProgress(sectionRef);
  const visibility = getScrollVisibility(progress);

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="relative py-24 md:py-32 text-white overflow-hidden scroll-mt-24"
    >
      {/* Background image */}
      <div className="absolute inset-0 z-0">
        <div className="w-full h-full bg-[url('../public/images/bg-consulting-agency.jpg')] bg-cover bg-center" />
      </div>

      {/* Overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/85 via-black/75 to-gray-950/80" />

      {/* Decorative shapes — move with scroll */}
      <div
        className="animate-drift pointer-events-none absolute -right-16 top-16 h-56 w-56 rounded-full bg-primary/[0.10] blur-3xl will-change-transform"
        style={{
          transform: `translate(${(1 - visibility) * 40}px, ${(1 - visibility) * -20}px)`,
        }}
      />
      <div
        className="animate-float pointer-events-none absolute -left-10 bottom-20 h-40 w-40 rounded-[3rem] border border-white/10 bg-white/[0.04] will-change-transform"
        style={{
          transform: `translate(${(1 - visibility) * -25}px, ${(1 - visibility) * 20}px)`,
        }}
      />
      <div
        className="animate-spin-slow pointer-events-none absolute right-[10%] bottom-12 h-14 w-14 rounded-full border border-white/15 border-dashed will-change-transform"
        style={{
          transform: `scale(${0.5 + visibility * 0.5}) rotate(${(1 - visibility) * 80}deg)`,
        }}
      />
      <div
        className="pointer-events-none absolute left-[12%] top-[18%] h-4 w-4 rounded-full bg-accent/40 will-change-transform"
        style={{ transform: `scale(${visibility})` }}
      />

      {/* Content */}
      <div className={`relative ${spacing.container} z-10`}>
        {/* Header — parallax fade up */}
        <div
          className="text-center mb-10 space-y-4 will-change-transform"
          style={{
            opacity: visibility,
            transform: `translateY(${(1 - visibility) * 30}px)`,
            transition: "transform 0.05s linear, opacity 0.1s linear",
          }}
        >
          <p className={`${typography.label} text-white/60`}>Contact</p>

          <h2 className={`${typography.heading.h2} text-white`}>
            Let's talk about your project
          </h2>

          <div className="flex justify-center">
            <div
              className="w-12 h-1 bg-gradient-to-r from-primary to-accent rounded-full"
              style={{ transform: `scaleX(${visibility})` }}
            />
          </div>

          <p
            className={`${typography.body.base} text-white/80 max-w-2xl mx-auto`}
          >
            Tell us briefly what you have in mind, and we'll get back to you
            with ideas, a rough timeline, and next steps.
          </p>
        </div>

        {/* Form */}
        <div
          className="max-w-2xl mx-auto will-change-transform"
          style={{
            opacity: visibility,
            transform: `translateY(${(1 - visibility) * 50}px) scale(${0.95 + visibility * 0.05})`,
            transition: "transform 0.05s linear, opacity 0.1s linear",
          }}
        >
          <ContactForm
            formData={formData}
            status={status}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            variant="compact"
          />
        </div>
      </div>
    </section>
  );
};

export default Contact;
